import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FileCheck, ArrowLeft, Plus, Search, FileDown, BarChart3, Calendar, User, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import FormularioGestion from './FormularioGestion';
import AccionesGestion from './AccionesGestion';
import DetalleGestion from './DetalleGestion';
import EditarGestion from './EditarGestion';
import EstadisticasModulo from './EstadisticasModulo';
import './ModuloBase.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ModuloIAP = ({ onBack }) => {
  const { user, canCreateCases, canEditCases, isAnalyst, canViewGestion } = useAuth();
  const [view, setView] = useState('list');
  const [gestiones, setGestiones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedGestion, setSelectedGestion] = useState(null);
  
  // Catálogos para filtros
  const [instituciones, setInstituciones] = useState([]);
  const [asesoresCNC, setAsesoresCNC] = useState([]);
  
  const [filtros, setFiltros] = useState({
    q: '',
    estado: '',
    institucion: '',
    asesor_cnc: '',
    fecha_asignacion_desde: '',
    fecha_asignacion_hasta: '',
    fecha_vencimiento_cnc_desde: '',
    fecha_vencimiento_cnc_hasta: '',
    fecha_vencimiento_probidad_desde: '',
    fecha_vencimiento_probidad_hasta: ''
  });

  useEffect(() => {
    fetchGestiones();
    fetchCatalogos();
  }, []);
  
  const fetchCatalogos = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      const [instResponse, asesoresResponse] = await Promise.all([
        axios.get(`${API}/catalogos/instituciones`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/catalogos/asesores-cnc?activo=true`, { headers }).catch(() => ({ data: [] }))
      ]);
      
      setInstituciones(instResponse.data || []);
      setAsesoresCNC(asesoresResponse.data || []);
    } catch (error) {
      console.error('Error al cargar catálogos:', error);
    }
  };

  const fetchGestiones = async () => {
    setLoading(true);
    try {
      // Buscar con ambos nombres para compatibilidad
      const [response1, response2] = await Promise.all([
        axios.get(`${API}/gestiones?tipo_gestion=Contratación Personal`).catch(() => ({ data: [] })),
        axios.get(`${API}/gestiones?tipo_gestion=Requerimientos de Instancias de Probidad`).catch(() => ({ data: [] })),
        axios.get(`${API}/gestiones?tipo_gestion=Requerimientos IA`).catch(() => ({ data: [] })),
        axios.get(`${API}/gestiones?tipo_gestion=Contratación Personal`).catch(() => ({ data: [] }))
      ]);
      let allGestiones = [...(response1.data || []), ...(response2.data || [])];
      let uniqueGestiones = allGestiones.filter((g, index, self) => 
        index === self.findIndex(t => t.id === g.id)
      );
      // Si es analista, filtrar solo sus gestiones
      if (isAnalyst()) {
        uniqueGestiones = uniqueGestiones.filter(g => canViewGestion(g));
      }
      setGestiones(uniqueGestiones);
    } catch (error) {
      console.error('Error al cargar gestiones:', error);
      toast.error('Error al cargar las gestiones');
    } finally {
      setLoading(false);
    }
  };

  const handleExportarExcel = () => {
    if (gestiones.length === 0) {
      toast.error('No hay datos para exportar');
      return;
    }

    const data = gestiones.map(g => ({
      'Nomenclatura': g.nomenclatura,
      'No. Oficio/Requerimiento': g.campos_especificos?.numero_oficio_requerimiento || 'N/A',
      'Fecha Solicitud': g.campos_especificos?.fecha_solicitud 
        ? new Date(g.campos_especificos.fecha_solicitud).toLocaleDateString('es-ES') 
        : 'N/A',
      'Solicitante': g.campos_especificos?.solicitante || 'N/A',
      'Cargo': g.campos_especificos?.cargo_solicitante || 'N/A',
      'Institución': g.institucion,
      'Estado': g.estado,
      'Asesor CNC': g.nombre_asesor_cnc,
      'Descripción': g.descripcion
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'RIP');
    
    const fecha = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `Contratacion_Personal_${fecha}.xlsx`);
    toast.success('Excel exportado correctamente');
  };

  const handleBuscar = () => {
    let result = [...gestiones];
    
    // Búsqueda por texto
    if (filtros.q) {
      const q = filtros.q.toLowerCase();
      result = result.filter(g => 
        g.nomenclatura?.toLowerCase().includes(q) ||
        g.descripcion?.toLowerCase().includes(q) ||
        g.institucion?.toLowerCase().includes(q) ||
        g.nombre_asesor_cnc?.toLowerCase().includes(q) ||
        g.campos_especificos?.solicitante?.toLowerCase().includes(q) ||
        g.campos_especificos?.numero_oficio_requerimiento?.toLowerCase().includes(q)
      );
    }
    
    // Filtro por estado
    if (filtros.estado) {
      result = result.filter(g => g.estado === filtros.estado);
    }
    
    // Filtro por institución
    if (filtros.institucion) {
      result = result.filter(g => g.institucion === filtros.institucion);
    }
    
    // Filtro por asesor CNC
    if (filtros.asesor_cnc) {
      result = result.filter(g => g.nombre_asesor_cnc === filtros.asesor_cnc);
    }
    
    // Filtro por fecha de asignación CNC
    if (filtros.fecha_asignacion_desde) {
      result = result.filter(g => g.fecha_asignacion && new Date(g.fecha_asignacion) >= new Date(filtros.fecha_asignacion_desde));
    }
    if (filtros.fecha_asignacion_hasta) {
      result = result.filter(g => g.fecha_asignacion && new Date(g.fecha_asignacion) <= new Date(filtros.fecha_asignacion_hasta));
    }
    
    // Filtro por fecha de vencimiento CNC
    if (filtros.fecha_vencimiento_cnc_desde) {
      result = result.filter(g => g.fecha_vencimiento && new Date(g.fecha_vencimiento) >= new Date(filtros.fecha_vencimiento_cnc_desde));
    }
    if (filtros.fecha_vencimiento_cnc_hasta) {
      result = result.filter(g => g.fecha_vencimiento && new Date(g.fecha_vencimiento) <= new Date(filtros.fecha_vencimiento_cnc_hasta));
    }
    
    // Filtro por fecha de vencimiento Instancia Probidad
    if (filtros.fecha_vencimiento_probidad_desde) {
      result = result.filter(g => g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) >= new Date(filtros.fecha_vencimiento_probidad_desde));
    }
    if (filtros.fecha_vencimiento_probidad_hasta) {
      result = result.filter(g => g.fecha_vencimiento_probidad && new Date(g.fecha_vencimiento_probidad) <= new Date(filtros.fecha_vencimiento_probidad_hasta));
    }
    
    setGestiones(result);
    toast.success(`${result.length} gestiones encontradas`);
  };

  const handleLimpiarFiltros = () => {
    setFiltros({
      q: '',
      estado: '',
      institucion: '',
      asesor_cnc: '',
      fecha_asignacion_desde: '',
      fecha_asignacion_hasta: '',
      fecha_vencimiento_cnc_desde: '',
      fecha_vencimiento_cnc_hasta: '',
      fecha_vencimiento_probidad_desde: '',
      fecha_vencimiento_probidad_hasta: ''
    });
    fetchGestiones();
  };

  const handleViewDetail = (gestion) => {
    setSelectedGestion(gestion);
    setView('detail');
  };

  const handleEditGestion = (gestion) => {
    setSelectedGestion(gestion);
    setView('edit');
  };

  const renderCamposEspecificos = (formData, handleChange) => (
    <div className="form-grid">
      <div className="form-field">
        <label className="required">Referencia</label>
        <input
          type="text"
          value={formData.campos_especificos.referencia || formData.campos_especificos.numero_oficio || ''}
          onChange={(e) => handleChange('referencia', e.target.value)}
          placeholder="Número de oficio o referencia"
          required
        />
      </div>

      <div className="form-field">
        <label>Fecha análisis desde</label>
        <input
          type="date"
          value={formData.campos_especificos.fecha_analisis_desde || ''}
          onChange={(e) => handleChange('fecha_analisis_desde', e.target.value)}
        />
      </div>

      <div className="form-field">
        <label>Fecha análisis hasta</label>
        <input
          type="date"
          value={formData.campos_especificos.fecha_analisis_hasta || ''}
          onChange={(e) => handleChange('fecha_analisis_hasta', e.target.value)}
        />
      </div>
    </div>
  );

  // Mostrar vista de detalle
  if (view === 'detail' && selectedGestion) {
    return (
      <div className="modulo-container">
        <DetalleGestion 
          gestionId={selectedGestion.id} 
          onBack={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
          }} 
        />
      </div>
    );
  }

  // Mostrar vista de edición
  if (view === 'edit' && selectedGestion) {
    return (
      <div className="modulo-container">
        <EditarGestion 
          gestion={selectedGestion}
          onSuccess={() => {
            setSelectedGestion(null);
            setView('list');
            fetchGestiones();
            toast.success('Gestión actualizada correctamente');
          }}
          onCancel={() => {
            setSelectedGestion(null);
            setView('list');
          }}
        />
      </div>
    );
  }

  return (
    <div className="modulo-container">
      <div className="modulo-header-bar">
        <button className="btn-back" onClick={onBack}>
          <ArrowLeft size={20} />
          Volver a Módulos
        </button>
        
        <div className="modulo-title-section">
          <div className="modulo-icon-header" style={{ backgroundColor: '#4CAF50' }}>
            <FileCheck size={24} />
          </div>
          <div>
            <h1 className="modulo-main-title">Contratación Personal</h1>
            <p className="modulo-nomenclatura">Nomenclatura: CDP-###</p>
          </div>
        </div>
      </div>

      <div className="modulo-tabs">
        <button 
          className={`modulo-tab ${view === 'list' ? 'active' : ''}`}
          onClick={() => setView('list')}
        >
          Listado de Gestiones
        </button>
        {canCreateCases() && (
          <button 
            className={`modulo-tab ${view === 'create' ? 'active' : ''}`}
            onClick={() => setView('create')}
          >
            <Plus size={16} />
            Nueva Gestión
          </button>
        )}
        <button 
          className={`modulo-tab ${view === 'stats' ? 'active' : ''}`}
          onClick={() => setView('stats')}
        >
          <BarChart3 size={16} />
          Estadísticas
        </button>
      </div>

      <div className="modulo-content">
        {view === 'list' && (
          <div>
            {/* Filtros Ampliados */}
            <div className="panel filters-compact" style={{ marginBottom: '1.5rem' }}>
              {/* Fila 1: Búsqueda principal y filtros de categoría */}
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="filter-item">
                  <label>Búsqueda General</label>
                  <input
                    type="text"
                    placeholder="Nomenclatura, solicitante, oficio..."
                    value={filtros.q}
                    onChange={(e) => setFiltros({...filtros, q: e.target.value})}
                  />
                </div>
                <div className="filter-item">
                  <label>Institución</label>
                  <select value={filtros.institucion} onChange={(e) => setFiltros({...filtros, institucion: e.target.value})}>
                    <option value="">Todas</option>
                    {instituciones.map(inst => (
                      <option key={inst.id} value={inst.nombre}>{inst.nombre}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Asesor CNC</label>
                  <select value={filtros.asesor_cnc} onChange={(e) => setFiltros({...filtros, asesor_cnc: e.target.value})}>
                    <option value="">Todos</option>
                    {asesoresCNC.map(asesor => (
                      <option key={asesor.id} value={asesor.nombre_completo}>{asesor.nombre_completo}</option>
                    ))}
                  </select>
                </div>
                <div className="filter-item">
                  <label>Estado</label>
                  <select value={filtros.estado} onChange={(e) => setFiltros({...filtros, estado: e.target.value})}>
                    <option value="">Todos</option>
                    <option value="Registrado">Registrado</option>
                    <option value="Asignada">Asignada</option>
                    <option value="En proceso">En proceso</option>
                    <option value="Enviado / Compartido">Enviado / Compartido</option>
                    <option value="En espera">En espera</option>
                    <option value="En revisión">En revisión</option>
                    <option value="Derivado a probidad">Derivado a probidad</option>
                    <option value="Completada / Archivada">Completada / Archivada</option>
                  </select>
                </div>
              </div>
              
              {/* Fila 2: Filtros de fechas */}
              <div className="filters-row" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <div className="filter-item">
                  <label>Asig. CNC Desde</label>
                  <input type="date" value={filtros.fecha_asignacion_desde} onChange={(e) => setFiltros({...filtros, fecha_asignacion_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Asig. CNC Hasta</label>
                  <input type="date" value={filtros.fecha_asignacion_hasta} onChange={(e) => setFiltros({...filtros, fecha_asignacion_hasta: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. CNC Desde</label>
                  <input type="date" value={filtros.fecha_vencimiento_cnc_desde} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_cnc_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. CNC Hasta</label>
                  <input type="date" value={filtros.fecha_vencimiento_cnc_hasta} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_cnc_hasta: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. IP Desde</label>
                  <input type="date" value={filtros.fecha_vencimiento_probidad_desde} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_probidad_desde: e.target.value})} />
                </div>
                <div className="filter-item">
                  <label>Venc. IP Hasta</label>
                  <input type="date" value={filtros.fecha_vencimiento_probidad_hasta} onChange={(e) => setFiltros({...filtros, fecha_vencimiento_probidad_hasta: e.target.value})} />
                </div>
              </div>
              
              {/* Fila 3: Botones */}
              <div className="filters-row" style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button className="btn btn-primary" onClick={handleBuscar}>
                  <Search size={16} />
                  Buscar
                </button>
                <button className="btn btn-secondary" onClick={handleLimpiarFiltros}>
                  Limpiar
                </button>
              </div>
            </div>

            <div className="panel">
              <div className="panel-header">
                <h2>Contratación Personal ({gestiones.length})</h2>
                <button className="btn btn-secondary" onClick={handleExportarExcel}>
                  <FileDown size={16} />
                  Exportar Excel
                </button>
              </div>

              {loading ? (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <div className="loader"></div>
                </div>
              ) : gestiones.length === 0 ? (
                <div className="empty-state">
                  <FileCheck size={48} style={{ opacity: 0.3 }} />
                  <p>No hay requerimientos IA registrados</p>
                  {canCreateCases() && (
                    <button className="btn btn-primary" onClick={() => setView('create')}>
                      <Plus size={16} />
                      Crear Primera Gestión
                    </button>
                  )}
                </div>
              ) : (
                <div className="table-wrapper">
                  <table className="detail-table">
                    <thead>
                      <tr>
                        <th>Nomenclatura</th>
                        <th>Referencia</th>
                        <th>Fecha Solicitud</th>
                        <th>Institución</th>
                        <th>Estado</th>
                        <th>Asesor CNC</th>
                        <th>Venc. CNC</th>
                        <th>Probidad</th>
                        <th>Asesor IP</th>
                        <th>Venc. IP</th>
                        <th>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {gestiones.map((gestion) => {
                        const hoy = new Date();
                        const fechaVencCNC = gestion.fecha_vencimiento ? new Date(gestion.fecha_vencimiento) : null;
                        const fechaVencIP = gestion.fecha_vencimiento_probidad ? new Date(gestion.fecha_vencimiento_probidad) : null;
                        const vencidoCNC = fechaVencCNC && fechaVencCNC < hoy && gestion.estado !== 'Completada / Archivada';
                        const vencidoIP = fechaVencIP && fechaVencIP < hoy && gestion.estado !== 'Completada / Archivada';
                        
                        return (
                          <tr key={gestion.id}>
                            <td><strong>{gestion.nomenclatura}</strong></td>
                            <td>{gestion.campos_especificos?.referencia || gestion.campos_especificos?.numero_oficio || 'N/A'}</td>
                            <td>
                              {gestion.campos_especificos?.fecha_solicitud || gestion.campos_especificos?.fecha_oficio
                                ? new Date(gestion.campos_especificos.fecha_solicitud || gestion.campos_especificos.fecha_oficio).toLocaleDateString('es-ES')
                                : 'N/A'
                              }
                            </td>
                            <td>{gestion.institucion}</td>
                            <td>
                              <span className={`badge ${gestion.estado === 'Completada / Archivada' ? 'badge-success' : gestion.estado === 'En proceso de análisis' ? 'badge-info' : 'badge-warning'}`}>
                                {gestion.estado}
                              </span>
                            </td>
                            <td>{gestion.nombre_asesor_cnc}</td>
                            <td style={{ color: vencidoCNC ? '#D32F2F' : 'inherit', fontWeight: vencidoCNC ? 600 : 'normal' }}>
                              {fechaVencCNC ? fechaVencCNC.toLocaleDateString('es-ES') : '-'}
                              {vencidoCNC && <AlertTriangle size={12} style={{ marginLeft: '4px', color: '#D32F2F' }} />}
                            </td>
                            <td>
                              {gestion.asesor_probidad_id ? (
                                <span className="badge badge-success" style={{ fontSize: '0.65rem' }}>Sí</span>
                              ) : (
                                <span className="badge badge-secondary" style={{ fontSize: '0.65rem' }}>No</span>
                              )}
                            </td>
                            <td style={{ fontSize: '0.8rem' }}>{gestion.asesor_probidad_nombre || '-'}</td>
                            <td style={{ color: vencidoIP ? '#D32F2F' : 'inherit', fontWeight: vencidoIP ? 600 : 'normal' }}>
                              {fechaVencIP ? fechaVencIP.toLocaleDateString('es-ES') : '-'}
                              {vencidoIP && <AlertTriangle size={12} style={{ marginLeft: '4px', color: '#D32F2F' }} />}
                            </td>
                            <td>
                              <AccionesGestion 
                                gestion={gestion}
                                onView={handleViewDetail}
                                onEdit={canEditCases() ? handleEditGestion : null}
                                compact={true}
                                canEdit={canEditCases()}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {view === 'create' && (
          <FormularioGestion
            tipoGestion="Contratación Personal"
            nomenclatura="CDP"
            camposEspecificos={renderCamposEspecificos}
            onSuccess={() => {
              fetchGestiones();
              setView('list');
            }}
            onCancel={() => setView('list')}
          />
        )}

        {view === 'stats' && (
          <EstadisticasModulo 
            tipoGestion="Contratación Personal" 
            colorModulo="#4CAF50"
          />
        )}
      </div>
    </div>
  );
};

export default ModuloIAP;

